/**
 * PATH: packages/email-hub/src/feature/components/InboxFilterPills.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-INBOXFILTERPILLS-001
 * PURPOSE: The single visible query strip under the inbox search bar —
 *          renders every active constraint as a removable pill plus a
 *          one-click "Clear all".
 * WHY:     Operator 2026-07-06 — filters were scattered across the
 *          search input (invisible tokens), chip bar, sidebar and
 *          footer; nothing answered "what am I filtering right now?".
 *          This strip is that answer. Presentational only: the pill
 *          list is built by lib/inboxFilterPills.ts and removal is
 *          dispatched by the page, so this file has zero filter logic.
 * FLOW:
 *   ┌────────────────┐   ┌──────────────────┐   ┌──────────────────┐
 *   │ pills[] +      │ → │ pill row (wrap)  │ → │ onRemove(id) /   │
 *   │ callbacks      │   │ + Clear all      │   │ onClearAll()     │
 *   └────────────────┘   └──────────────────┘   └──────────────────┘
 * RELATED:
 *   - lib/inboxFilterPills.ts: buildInboxFilterPills (pill model)
 *   - pages/InboxPage.tsx: state owner, mounts this strip
 */

import { X } from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import type { FilterPill } from '../lib/inboxFilterPills'

export interface InboxFilterPillsProps {
  pills: FilterPill[]
  /** Remove one pill by its stable id (dispatch lives in InboxPage). */
  onRemove: (id: string) => void
  /** Reset every query-narrowing filter (standing prefs untouched). */
  onClearAll: () => void
}

/** Renders nothing when no constraint is active — zero chrome at rest. */
export function InboxFilterPills({ pills, onRemove, onClearAll }: InboxFilterPillsProps) {
  if (pills.length === 0) return null
  const hasFilterPills = pills.some((p) => p.kind === 'filter')
  return (
    <div
      className="flex items-center gap-1.5 mt-2 flex-wrap"
      data-testid="inbox-filter-pills"
      role="group"
      aria-label="Active filters"
    >
      {pills.map((pill) => (
        <span
          key={pill.id}
          data-testid={`filter-pill-${pill.id}`}
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px]',
            pill.kind === 'filter'
              ? 'bg-primary/10 text-primary'
              : 'bg-muted text-muted-foreground border border-dashed border-border',
          )}
        >
          {pill.label}
          <button
            type="button"
            onClick={() => onRemove(pill.id)}
            title={pill.title}
            aria-label={pill.title}
            className="hover:opacity-70"
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </span>
      ))}
      {hasFilterPills && (
        <button
          type="button"
          onClick={onClearAll}
          className="text-[10px] text-muted-foreground hover:text-foreground hover:underline px-1"
          data-testid="inbox-filter-clear-all"
        >
          Clear all
        </button>
      )}
    </div>
  )
}
